import * as React from "react";
import { Box, Button, Card, CardActionArea, Chip, Grid, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { Status } from "../../enums/enum";
import { useAppDispatch, useAppSelector } from "../../Hooks";
import { getUserRequestAll } from "../../store/user-process/selector";
import UserRequestCreate from "../../Components/dialog/user-request-create/user-request-create";
import { GetUserRequestAll } from "../../services/api-actions/personal-user-request-action";
import { UserRequest } from "../../types/user-info/user-request";
import PageLayout from "../../Components/page-layout/PageLayout";
import { brand } from "../../theme";

function statusChip(id: number, text: string) {
  const color = id === Status.Success ? "success" : id === Status.Cansel ? "error" : id === Status.InWork ? "warning" : "info";
  return <Chip size="small" color={color} label={text} />;
}

function RequestCard({ item }: { item: UserRequest }) {
  return (
    <Card sx={{ mb: 1.5 }}>
      <CardActionArea component={Link} to={`../UserRequestChat/${item.requestId}`} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
              {statusChip(item.status.id, item.status.status)}
              {item.lic && (
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  Л/с {item.lic}
                </Typography>
              )}
              {item.createdDate && (
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  · {dayjs(item.createdDate).format("DD.MM.YYYY")}
                </Typography>
              )}
            </Stack>
            <Typography sx={{ fontWeight: 600 }}>{item.subject.subject}</Typography>
            {item.message && (
              <Typography color="text.secondary" noWrap sx={{ fontSize: 14 }}>
                {item.message}
              </Typography>
            )}
          </Box>
          <ChevronRightIcon sx={{ color: brand.muted }} />
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export default function UserRequests() {
  const dispatch = useAppDispatch();
  const [openDialog, setOpenDialog] = React.useState(false);

  React.useEffect(() => {
    dispatch(GetUserRequestAll());
  }, [dispatch]);

  const userRequest = useAppSelector(getUserRequestAll);
  const active = userRequest?.filter((x) => x.status.id == Status.Create || x.status.id == Status.InWork) ?? [];
  const closed = userRequest?.filter((x) => x.status.id == Status.Cansel || x.status.id == Status.Success) ?? [];

  return (
    <PageLayout
      title="Мои обращения"
      subtitle="Вопросы и заявки в расчётный центр"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Создать обращение
        </Button>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Активные
          </Typography>
          {active.length > 0 ? (
            active.map((item) => <RequestCard key={item.requestId} item={item} />)
          ) : (
            <Card sx={{ textAlign: "center", py: 4, px: 2 }}>
              <ForumOutlinedIcon sx={{ fontSize: 40, color: brand.muted }} />
              <Typography sx={{ mt: 1, fontWeight: 600 }}>Активных обращений нет</Typography>
              <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                Если у вас есть вопрос, создайте обращение — мы ответим здесь же.
              </Typography>
            </Card>
          )}

          {closed.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>
                История
              </Typography>
              {closed.map((item) => (
                <RequestCard key={item.requestId} item={item} />
              ))}
            </>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2.5, bgcolor: brand.background }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Когда ждать ответ
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 14.5, lineHeight: 1.6 }}>
              Мы постараемся ответить Вам в кратчайшие сроки.
              <br />
              <br />
              Если вы забыли рассказать что-то важное, просто оставьте комментарий на странице обращения — мы увидим его.
              <br />
              <br />
              Пожалуйста, не отправляйте сообщение повторно и не перезванивайте с тем же вопросом в колл-центр — поток новых обращений увеличит время ответа.
            </Typography>
          </Card>
        </Grid>
      </Grid>
      <UserRequestCreate Open={openDialog} Close={setOpenDialog} />
    </PageLayout>
  );
}
